import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from "next";
import type { PostOrPage } from "@tryghost/content-api";
import { getSinglePost, getAllPosts } from "@/lib/ghost";
import { formatDate } from "@/lib/utils";
import { HeroSection } from "@/components/shared/HeroSection";
import Footer from "@/components/shared/Footer";
import { BlogSeo } from "@/components/blog/BlogSeo";
import { BlogBackButton } from "@/components/blog/BlogBackButton";
import { BlogPostContent } from "@/components/blog/BlogPostContent";

const getPostParams = (post: PostOrPage) => ({
  params: { slug: post.slug },
});

export const getStaticPaths = (async () => {
  const posts = await getAllPosts();

  return {
    paths: posts?.map(getPostParams) ?? [],
    fallback: "blocking",
  };
}) satisfies GetStaticPaths;

export const getStaticProps = (async (context: GetStaticPropsContext) => {
  if (!context.params?.slug || typeof context.params.slug !== "string") {
    return { notFound: true };
  }

  const post = await getSinglePost(context.params.slug);

  if (!post) {
    return { notFound: true };
  }

  return {
    props: { post },
    revalidate: 86400, // daily
  };
}) satisfies GetStaticProps<{
  post: PostOrPage;
}>;

export default function BlogPostPage({ post }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <>
      <BlogSeo post={post} />
      <HeroSection
        bgPattern={false}
        topComponent={
          <div className="flex flex-col items-center justify-between space-y-5">
            <BlogBackButton />
            <div className="flex w-full justify-center">
              <div className="w-fit rounded-full border border-gray-200 bg-gray-100 px-3.5 py-1 text-center text-sm text-gray-900">
                {post?.tags?.[0]?.name ?? post?.custom_excerpt ?? "Faceless Content Creation"}
              </div>
            </div>
          </div>
        }
        heading={
          <h1 className="text-center text-4xl font-semibold text-gray-900 lg:text-balance lg:tracking-[0.015em]">
            {post?.title || ""}
          </h1>
        }
        body={
          <div className="flex items-center justify-between space-x-2 text-sm text-gray-600">
            {post?.published_at && <div>{formatDate(post.published_at)}</div>}
            <span>•</span>
            <span>Danny G.</span>
          </div>
        }
      >
        <BlogPostContent post={post} />
      </HeroSection>
      <Footer />
    </>
  );
}
